import { downloadBlob, filenameFromDisposition } from '../utils/kernelDownload';
import api, { API_URL, API_ORIGIN, assetUrl, errorMessage } from './kernelApi';

export { API_URL, API_ORIGIN, assetUrl, errorMessage };
export * from './kernelApiCommon';

export const getDashboard = async () => {
  const response = await api.get('/dashboard');
  return response.data;
};

// Statistiche per la dashboard dei ticket.
export const getTicketDashboard = async () => {
  const response = await api.get('/ticket-dashboard');
  return response.data;
};

// --- Ticket (staff) ---

export const getTickets = async (filters = {}) => {
  const response = await api.get('/tickets', { params: filters });
  return response.data;
};

export const getTicket = async (id) => {
  const response = await api.get(`/tickets/${id}`);
  return response.data;
};

export const createTicket = async (data) => {
  const response = await api.post('/tickets', data);
  return response.data;
};

export const updateTicket = async (id, data) => {
  const response = await api.put(`/tickets/${id}`, data);
  return response.data;
};

export const updateTicketStatus = async (id, status) => {
  const response = await api.put(`/tickets/${id}/status`, { status });
  return response.data;
};

export const addTicketComment = async (id, body, isInternalNote = false) => {
  const response = await api.post(`/tickets/${id}/comments`, { body, is_internal_note: isInternalNote });
  return response.data;
};

// Bozze di risposta al cliente (da rivedere e inviare)
export const createTicketDraft = async (id, body) => {
  const response = await api.post(`/tickets/${id}/drafts`, { body });
  return response.data;
};

export const updateTicketDraft = async (draftId, body) => {
  const response = await api.put(`/ticket-drafts/${draftId}`, { body });
  return response.data;
};

export const deleteTicketDraft = async (draftId) => {
  const response = await api.delete(`/ticket-drafts/${draftId}`);
  return response.data;
};

export const sendTicketDraft = async (draftId, body) => {
  const response = await api.post(`/ticket-drafts/${draftId}/send`, body ? { body } : {});
  return response.data;
};

export const uploadTicketAttachment = async (id, files) => {
  const formData = new FormData();
  (Array.isArray(files) ? files : [files]).forEach((file) => formData.append('files[]', file));
  const response = await api.post(`/tickets/${id}/attachments`, formData, {
    headers: { 'Content-Type': undefined },
  });
  return response.data;
};

// L'endpoint richiede il Bearer token (vedi interceptor sopra), quindi un
// <img src> o <a href> puntato direttamente all'URL dell'API non si
// autentica - va sempre scaricato via axios come blob e poi trasformato in
// un object URL locale, sia per l'anteprima sia per l'apertura/il download.
export const fetchTicketAttachmentBlob = async (ticketId, attachmentId) => {
  const response = await api.get(`/tickets/${ticketId}/attachments/${attachmentId}`, { responseType: 'blob' });
  return response.data;
};

export const downloadTicketAttachment = async (ticketId, attachmentId, filename) => {
  const blob = await fetchTicketAttachmentBlob(ticketId, attachmentId);
  downloadBlob(blob, filename || 'allegato');
};

// --- Code di assistenza (progetti ticket) ---

export const getTicketProjects = async () => {
  const response = await api.get('/ticket-projects');
  return response.data;
};

export const createTicketProject = async (data) => {
  const response = await api.post('/ticket-projects', data);
  return response.data;
};

export const linkTicketProject = async (id, projectId) => {
  const response = await api.put(`/ticket-projects/${id}/link`, { project_id: projectId });
  return response.data;
};

export const updateTicketProject = async (id, data) => {
  const response = await api.put(`/ticket-projects/${id}`, data);
  return response.data;
};

export const deleteTicketProject = async (id) => {
  const response = await api.delete(`/ticket-projects/${id}`);
  return response.data;
};

// --- Form builder: campi del form per progetto ---

export const getTicketProjectFields = async (projectId) => {
  const response = await api.get(`/ticket-projects/${projectId}/fields`);
  return response.data;
};

export const createTicketProjectField = async (projectId, data) => {
  const response = await api.post(`/ticket-projects/${projectId}/fields`, data);
  return response.data;
};

export const updateTicketProjectField = async (fieldId, data) => {
  const response = await api.put(`/ticket-project-fields/${fieldId}`, data);
  return response.data;
};

export const deleteTicketProjectField = async (fieldId) => {
  const response = await api.delete(`/ticket-project-fields/${fieldId}`);
  return response.data;
};

export const reorderTicketProjectFields = async (projectId, orderedIds) => {
  const response = await api.put(`/ticket-projects/${projectId}/fields/reorder`, { ordered_ids: orderedIds });
  return response.data;
};

export const getTicketFieldsLibrary = async () => {
  const response = await api.get('/ticket-fields');
  return response.data;
};

export const addTicketProjectFieldFromLibrary = async (projectId, fieldId) => {
  const response = await api.post(`/ticket-projects/${projectId}/fields/from-library/${fieldId}`);
  return response.data;
};

// --- Progetti (tabelle di attività) ---

// state: 'active' (default), 'archived' o 'trash'.
export const getProjects = async (state = 'active') => {
  const response = await api.get('/projects', { params: { state } });
  return response.data;
};

// Progetto con colonne e attività.
export const getProject = async (id, lang) => {
  const response = await api.get(`/projects/${id}`, { params: { lang } });
  return response.data;
};

export const createProject = async (data) => {
  const response = await api.post('/projects', data);
  return response.data;
};

export const updateProject = async (id, data) => {
  const response = await api.put(`/projects/${id}`, data);
  return response.data;
};

export const duplicateProject = async (id, data = {}) => {
  const response = await api.post(`/projects/${id}/duplicate`, data);
  return response.data;
};

// Nel cestino (ripristinabile); con permanent: eliminazione definitiva.
export const deleteProject = async (id, permanent = false) => {
  const response = await api.delete(`/projects/${id}`, { params: permanent ? { permanent: 1 } : {} });
  return response.data;
};

export const restoreProject = async (id) => {
  const response = await api.post(`/projects/${id}/restore`);
  return response.data;
};

export const archiveProject = async (id, archived = true) => {
  const response = await api.post(`/projects/${id}/${archived ? 'archive' : 'unarchive'}`);
  return response.data;
};

export const setProjectFavorite = async (id, favorite) => {
  const response = favorite ? await api.post(`/projects/${id}/favorite`) : await api.delete(`/projects/${id}/favorite`);
  return response.data;
};

export const reorderProjects = async (orderedIds) => {
  const response = await api.put('/projects/reorder', { ordered_ids: orderedIds });
  return response.data;
};

export const searchProjects = async (q) => {
  const response = await api.get('/project-search', { params: { q } });
  return response.data;
};

export const createProjectColumn = async (projectId, data) => {
  const response = await api.post(`/projects/${projectId}/columns`, data);
  return response.data;
};

export const updateProjectColumn = async (id, data) => {
  const response = await api.put(`/project-columns/${id}`, data);
  return response.data;
};

export const deleteProjectColumn = async (id) => {
  const response = await api.delete(`/project-columns/${id}`);
  return response.data;
};

export const duplicateProjectColumn = async (id) => {
  const response = await api.post(`/project-columns/${id}/duplicate`);
  return response.data;
};

export const createProjectView = async (projectId, data) => {
  const response = await api.post(`/projects/${projectId}/views`, data);
  return response.data;
};

export const updateProjectView = async (id, data) => {
  const response = await api.put(`/project-views/${id}`, data);
  return response.data;
};

export const deleteProjectView = async (id) => {
  const response = await api.delete(`/project-views/${id}`);
  return response.data;
};

export const uploadProjectTaskFiles = async (taskId, columnId, files) => {
  const formData = new FormData();
  formData.append('column_id', String(columnId));
  files.forEach((file) => formData.append('files[]', file));
  const response = await api.post(`/project-tasks/${taskId}/files`, formData, { headers: { 'Content-Type': undefined } });
  return response.data;
};

export const deleteProjectTaskFile = async (taskId, fileId) => {
  const response = await api.delete(`/project-tasks/${taskId}/files/${fileId}`);
  return response.data;
};

// Contenuto di un file come blob (per le anteprime).
export const fetchProjectTaskFileBlob = async (taskId, fileId) => {
  const response = await api.get(`/project-tasks/${taskId}/files/${fileId}`, { responseType: 'blob' });
  return response.data;
};

// Il download richiede il Bearer token: si scarica come blob e si apre da un link locale.
export const downloadProjectTaskFile = async (taskId, file) => {
  const response = await api.get(`/project-tasks/${taskId}/files/${file.id}`, { responseType: 'blob' });
  downloadBlob(response.data, file.name || 'file');
};

// Attività completa: corpo e commenti (la lista dei progetti non li include).
export const getProjectTask = async (id) => {
  const response = await api.get(`/project-tasks/${id}`);
  return response.data;
};

export const addProjectTaskComment = async (taskId, body) => {
  const response = await api.post(`/project-tasks/${taskId}/comments`, { body });
  return response.data;
};

export const deleteProjectTaskComment = async (taskId, commentId) => {
  const response = await api.delete(`/project-tasks/${taskId}/comments/${commentId}`);
  return response.data;
};

export const createProjectTask = async (projectId, data) => {
  const response = await api.post(`/projects/${projectId}/tasks`, data);
  return response.data;
};

export const updateProjectTask = async (id, data) => {
  const response = await api.put(`/project-tasks/${id}`, data);
  return response.data;
};

// Nel cestino (con le sotto-attività); con permanent: eliminazione definitiva.
export const deleteProjectTask = async (id, permanent = false) => {
  const response = await api.delete(`/project-tasks/${id}`, { params: permanent ? { permanent: 1 } : {} });
  return response.data;
};

export const restoreProjectTask = async (id) => {
  const response = await api.post(`/project-tasks/${id}/restore`);
  return response.data;
};

export const duplicateProjectTask = async (id) => {
  const response = await api.post(`/project-tasks/${id}/duplicate`);
  return response.data;
};

export const reorderProjectTasks = async (projectId, orderedIds) => {
  const response = await api.put(`/projects/${projectId}/tasks/reorder`, { ordered_ids: orderedIds });
  return response.data;
};

// action: 'delete' | 'duplicate' | 'set' (con data).
export const bulkProjectTasks = async (projectId, ids, action, data) => {
  const response = await api.post(`/projects/${projectId}/tasks/bulk`, { ids, action, data });
  return response.data;
};

// Incolla da Excel / import CSV: righe {task_id?, cells: {id colonna | 'title': testo}}.
export const pasteProjectTasks = async (projectId, rows, createOptions = false) => {
  const response = await api.post(`/projects/${projectId}/tasks/paste`, { rows, create_options: createOptions });
  return response.data;
};

export const createProjectTaskTemplate = async (projectId, taskId, name) => {
  const response = await api.post(`/projects/${projectId}/task-templates`, { task_id: taskId, name });
  return response.data;
};

export const renameProjectTaskTemplate = async (id, name) => {
  const response = await api.put(`/project-task-templates/${id}`, { name });
  return response.data;
};

export const deleteProjectTaskTemplate = async (id) => {
  const response = await api.delete(`/project-task-templates/${id}`);
  return response.data;
};

export const getProjectTrash = async (projectId) => {
  const response = await api.get(`/projects/${projectId}/trash`);
  return response.data;
};

export const emptyProjectTrash = async (projectId) => {
  const response = await api.delete(`/projects/${projectId}/trash`);
  return response.data;
};

export const linkProjectTaskTicket = async (taskId, ticketId) => {
  const response = await api.post(`/project-tasks/${taskId}/tickets`, { ticket_id: ticketId });
  return response.data;
};

export const unlinkProjectTaskTicket = async (taskId, ticketId) => {
  const response = await api.delete(`/project-tasks/${taskId}/tickets/${ticketId}`);
  return response.data;
};

export const searchProjectTickets = async (q) => {
  const response = await api.get('/project-tickets/search', { params: { q } });
  return response.data;
};

// Attività dei progetti collegate a un ticket.
export const getTicketProjectTasks = async (ticketId) => {
  const response = await api.get(`/tickets/${ticketId}/project-tasks`);
  return response.data;
};

// --- Clienti ---

export const getCustomers = async () => {
  const response = await api.get('/customers');
  return response.data;
};

export const getCustomerTickets = async (customerId) => {
  const response = await api.get(`/customers/${customerId}/tickets`);
  return response.data;
};

// Gruppi di clienti (più indirizzi email dello stesso cliente, raggruppati a mano).
// payload: { customer_ids, group_id } oppure { customer_ids, new_group_name }; group_id nullo = toglie dal gruppo.
export const assignCustomerGroup = async (payload) => {
  const response = await api.put('/customers/group', payload);
  return response.data;
};

export const createCustomerGroup = async (payload) => {
  const response = await api.post('/customer-groups', payload);
  return response.data;
};

export const updateCustomerGroup = async (groupId, payload) => {
  const response = await api.put(`/customer-groups/${groupId}`, payload);
  return response.data;
};

export const deleteCustomerGroup = async (groupId) => {
  const response = await api.delete(`/customer-groups/${groupId}`);
  return response.data;
};

// --- Chiave API in ingresso (email → ticket) ---

export const generateProjectInboundKey = async (projectId) => {
  const response = await api.post(`/ticket-projects/${projectId}/inbound-key`);
  return response.data;
};

export const revokeProjectInboundKey = async (projectId) => {
  const response = await api.delete(`/ticket-projects/${projectId}/inbound-key`);
  return response.data;
};

// --- Modelli di progetto ---

export const getProjectTemplates = async () => {
  const response = await api.get('/project-templates');
  return response.data;
};

export const applyProjectTemplate = async (id, data) => {
  const response = await api.post(`/project-templates/${id}/apply`, data);
  return response.data;
};

// --- Notifiche per lo staff (campanello) ---

// --- Report (giornaliero / settimanale), Excel e report via email ---

export const getCsatReport = async (params) => {
  const response = await api.get('/reports/csat', { params });
  return response.data;
};

export const getSlaReport = async () => {
  const response = await api.get('/reports/sla');
  return response.data;
};

export const getDailyReport = async (params) => {
  const response = await api.get('/reports/daily', { params });
  return response.data;
};

export const getWeeklyReport = async (params) => {
  const response = await api.get('/reports/weekly', { params });
  return response.data;
};

export const downloadReportExcel = async (params) => {
  const response = await api.get('/reports/export', { params, responseType: 'blob' });
  downloadBlob(response.data, filenameFromDisposition(response.headers, 'report.xlsx'));
};

export const getReportSubscriptions = async () => {
  const response = await api.get('/report-subscriptions');
  return response.data;
};

export const saveReportSubscription = async (kind, data) => {
  const response = await api.put(`/report-subscriptions/${kind}`, data);
  return response.data;
};

export const sendReportNow = async (kind) => {
  const response = await api.post(`/report-subscriptions/${kind}/send-now`);
  return response.data;
};

// --- Risposte pronte e ticket simili ---

export const getCannedResponses = async (projectId) => {
  const response = await api.get('/canned-responses', { params: projectId ? { project_id: projectId } : {} });
  return response.data;
};

export const createCannedResponse = async (data) => {
  const response = await api.post('/canned-responses', data);
  return response.data;
};

export const updateCannedResponse = async (id, data) => {
  const response = await api.put(`/canned-responses/${id}`, data);
  return response.data;
};

export const deleteCannedResponse = async (id) => {
  const response = await api.delete(`/canned-responses/${id}`);
  return response.data;
};

export const getSimilarTickets = async (q, excludeId) => {
  const response = await api.get('/tickets/similar', { params: { q, ...(excludeId ? { exclude_id: excludeId } : {}) } });
  return response.data;
};

// --- Connettore Claude (token personali) ---

// --- Automazioni (webhook su evento ticket) ---

export const getAutomations = async () => {
  const response = await api.get('/automations');
  return response.data;
};

export const createAutomation = async (data) => {
  const response = await api.post('/automations', data);
  return response.data;
};

export const updateAutomation = async (id, data) => {
  const response = await api.put(`/automations/${id}`, data);
  return response.data;
};

export const deleteAutomation = async (id) => {
  const response = await api.delete(`/automations/${id}`);
  return response.data;
};

export const regenerateAutomationSecret = async (id) => {
  const response = await api.post(`/automations/${id}/regenerate-secret`);
  return response.data;
};

export const getAutomationRuns = async (id) => {
  const response = await api.get(`/automations/${id}/runs`);
  return response.data;
};

export default api;

// --- Riunioni (trascrizione + riassunto) ---
export const getMeetingCapabilities = async () => {
  const response = await api.get('/meetings/capabilities');
  return response.data.capabilities;
};

export const getMeetings = async () => {
  const response = await api.get('/meetings');
  return response.data;
};

export const getMeeting = async (id) => {
  const response = await api.get(`/meetings/${id}`);
  return response.data;
};

// Con un file audio la richiesta è multipart; altrimenti JSON con la trascrizione incollata.
export const createMeeting = async ({ audio, ...fields }) => {
  if (audio) {
    const formData = new FormData();
    Object.entries(fields).forEach(([key, value]) => formData.append(key, value ?? ''));
    formData.append('audio', audio);
    const response = await api.post('/meetings', formData, { headers: { 'Content-Type': undefined } });
    return response.data;
  }
  const response = await api.post('/meetings', fields);
  return response.data;
};

export const updateMeeting = async (id, data) => {
  const response = await api.put(`/meetings/${id}`, data);
  return response.data;
};

export const summarizeMeeting = async (id) => {
  const response = await api.post(`/meetings/${id}/summarize`);
  return response.data;
};

export const deleteMeeting = async (id) => {
  const response = await api.delete(`/meetings/${id}`);
  return response.data;
};

// --- Casella email del progetto (email in ingresso -> ticket, risposte inviate da lì) ---

export const getProjectMailbox = async (projectId) => {
  const response = await api.get(`/ticket-projects/${projectId}/mailbox`);
  return response.data;
};

export const saveProjectMailbox = async (projectId, data) => {
  const response = await api.put(`/ticket-projects/${projectId}/mailbox`, data);
  return response.data;
};

export const deleteProjectMailbox = async (projectId) => {
  const response = await api.delete(`/ticket-projects/${projectId}/mailbox`);
  return response.data;
};

export const testProjectMailbox = async (projectId, data) => {
  const response = await api.post(`/ticket-projects/${projectId}/mailbox/test`, data);
  return response.data;
};

export const checkProjectMailbox = async (projectId) => {
  const response = await api.post(`/ticket-projects/${projectId}/mailbox/check`);
  return response.data;
};

export const testMailboxFilters = async (projectId, data) => {
  const response = await api.post(`/ticket-projects/${projectId}/mailbox/filter-test`, data);
  return response.data;
};

// Etichette, viste salvate e unione dei ticket
export const getTicketTags = async () => (await api.get('/ticket-tags')).data;
export const createTicketTag = async (data) => (await api.post('/ticket-tags', data)).data;
export const updateTicketTag = async (id, data) => (await api.put(`/ticket-tags/${id}`, data)).data;
export const deleteTicketTag = async (id) => (await api.delete(`/ticket-tags/${id}`)).data;
export const setTicketTags = async (id, tagIds) => (await api.put(`/tickets/${id}/tags`, { tag_ids: tagIds })).data;
export const getTicketViews = async () => (await api.get('/ticket-views')).data;
export const createTicketView = async (data) => (await api.post('/ticket-views', data)).data;
export const deleteTicketView = async (id) => (await api.delete(`/ticket-views/${id}`)).data;
export const mergeTicket = async (id, targetId) => (await api.post(`/tickets/${id}/merge`, { target_id: targetId })).data;

// Regole interne e SLA configurabili
export const getTicketRules = async () => (await api.get('/ticket-rules')).data;
export const createTicketRule = async (data) => (await api.post('/ticket-rules', data)).data;
export const updateTicketRule = async (id, data) => (await api.put(`/ticket-rules/${id}`, data)).data;
export const deleteTicketRule = async (id) => (await api.delete(`/ticket-rules/${id}`)).data;
export const reorderTicketRules = async (ids) => (await api.put('/ticket-rules/order', { ids })).data;
export const getSlaPolicies = async () => (await api.get('/ticket-sla')).data;
export const recalculateSla = async () => (await api.post('/ticket-sla/recalculate')).data;
export const saveSlaPolicies = async (policies, calendars, holidays) => (await api.put('/ticket-sla', { policies, calendars, holidays })).data;

// Base di conoscenza
export const getKbArticles = async (params = {}) => (await api.get('/kb-articles', { params })).data;
export const getKbArticle = async (id) => (await api.get(`/kb-articles/${id}`)).data;
export const createKbArticle = async (data) => (await api.post('/kb-articles', data)).data;
export const updateKbArticle = async (id, data) => (await api.put(`/kb-articles/${id}`, data)).data;
export const deleteKbArticle = async (id) => (await api.delete(`/kb-articles/${id}`)).data;

// Privacy dei clienti (GDPR)
export const exportCustomerData = async (id) => (await api.get(`/customers/${id}/export`, { responseType: 'blob' })).data;
export const eraseCustomer = async (id, data) => (await api.post(`/customers/${id}/erase`, data)).data;

